import InfiniteScroll from 'react-infinite-scroll-component';
import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import omit from 'lodash/omit';
import { useCallback, useContext, useEffect, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import ArchiveSwitch from '@/modules/common/components/ArchiveSwitch.tsx';
import IncidentTypeItem from '@/modules/common/components/items/IncidentTypeItem.tsx';
import CreateIncidentModal from '@/modules/common/components/modals/CreateIncidentModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useIncidentsTypesListQuery } from '@/modules/common/queries/incidents.ts';
import IncidentTypeModal from '@/modules/admin-dashboard/components/modals/IncidentTypeModal.tsx';
import {
  Button,
  CalendarInput,
  CalendarInputProps,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  Spinner,
} from 'ui';
import { getDisabledDate } from 'ui/utils';

interface IncidentsProps {
  hideAddIncidentTypeButton?: boolean;
  clientId?: number;
}

interface Filters {
  from_date?: string;
  to_date?: string;
  filter_by_deleted?: boolean;
  filter_by_client_id?: number;
}

const Incidents = ({ hideAddIncidentTypeButton = false, clientId }: IncidentsProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const businessId = useGetCurrentBusinessId();
  const isUserAdmin = rolesManipulator.current_user_role === Role.COMPLIANCE_ADMIN;
  const [filters, setFilters] = useState<Filters>({
    ...(clientId ? { filter_by_client_id: clientId } : {}),
  });
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setFilters(clientId ? { filter_by_client_id: clientId } : {});
    setSearchValue('');
  }, [businessId, clientId]);

  const { data, isLoading, hasNextPage, fetchNextPage } = useIncidentsTypesListQuery({
    page: 1,
    page_size: 18,
    ...filters,
    ...(searchValue ? { filter_by_name: searchValue } : {}),
  });

  const incidentsTypes = (data?.pages || []).flatMap(({ data }) => data);

  const handleOpenCreateIncidentTypeModal = useCallback(() => {
    openModal(IncidentTypeModal);
  }, [openModal]);

  const handleOpenCreateIncidentModal = useCallback(() => {
    openModal(CreateIncidentModal, { clientId });
  }, [openModal, clientId]);

  const handleChangeDateFilter: CalendarInputProps['rangeOnChange'] = dates => {
    if (dates) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');
      setFilters(filters =>
        start_date && end_date
          ? { ...filters, from_date: start_date, to_date: end_date }
          : omit(filters, ['from_date', 'to_date']),
      );
      return;
    }
    setFilters(filters => omit(filters, ['from_date', 'to_date']));
  };

  const handleToggleArchiveFilter = useCallback(
    (active: boolean) => {
      setFilters(filters =>
        active ? { ...filters, filter_by_deleted: true } : omit(filters, ['filter_by_deleted']),
      );
    },
    [setFilters],
  );

  const headerAction = (
    <div className="flex max-w-[820px] [&>*]:h-10 [&>*]:max-w-[200px] flex-wrap-reverse w-full mt-5 md:mt-0 justify-end items-center gap-3">
      {isUserAdmin && (
        <ArchiveSwitch active={!!filters.filter_by_deleted} onChange={handleToggleArchiveFilter} />
      )}

      <div className="flex-1 min-w-[120px]">
        <SearchField placeholder="Search" value={searchValue} onChange={setSearchValue} />
      </div>
      <CalendarInput
        range
        allowClear
        rangeOnChange={handleChangeDateFilter}
        rangeValue={
          filters.from_date && filters.to_date
            ? [dayjs(filters.from_date), dayjs(filters.to_date)]
            : undefined
        }
        disabledDate={getDisabledDate}
        className="border rounded px-3 justify-center min-w-[240px] max-w-[250px] [&>div]:p-0 [&>div]:border-0"
      />
      {!hideAddIncidentTypeButton && (
        <div className="flex-1 min-w-fit">
          <Button variant="secondary" onClick={handleOpenCreateIncidentTypeModal}>
            Add event type
          </Button>
        </div>
      )}
      <div className="flex-1 min-w-fit">
        <Button
          onClick={handleOpenCreateIncidentModal}
          disabled={!clientId && !incidentsTypes.length}
        >
          New event
        </Button>
      </div>
    </div>
  );

  return (
    <PageLayout title="Events" headerAction={headerAction}>
      <EmptyListState
        title="No event types yet"
        description={
          'To create a type, click New Event Type (e.g. Breach event, Training event). ' +
          'Event types are needed for your business employees to report occurring events.'
        }
        isLoading={isLoading}
        isSearchUsed={!!searchValue || !!Object.values(filters).length}
        itemsCount={incidentsTypes.length}
      />
      {isLoading && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && !!incidentsTypes.length && (
        <InfiniteScroll
          className="flex flex-col gap-2"
          dataLength={incidentsTypes.length}
          hasMore={!!hasNextPage}
          // style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
          next={fetchNextPage}
        >
          {incidentsTypes.map((incident, index) => (
            <IncidentTypeItem
              incidentType={incident}
              key={index}
              archived={filters.filter_by_deleted}
              filterByName={searchValue}
              clientId={clientId}
            />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(Incidents, [Role.COMPLIANCE_ADMIN, Role.COMPLIANCE_USER]);
